import { keepPreviousData, useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../../core/network/api';
import { ticketKeys } from '../queries/keys';
import { ticketPaths } from '../queries/paths';
import { TICKET_STATES } from '../utils/tickets';

// TSS vite/03 — el estado de servidor de la ventana de tickets.
//
// EL LISTADO YA TRAE EL TICKET ENTERO, y eso decide la forma de la pantalla.
// `GET /admin/tickets` corre un `SELECT k.*` sobre la misma vista que el
// detalle: cada fila llega con su `body`, su nota de clasificación y su nota de
// resolución. Así que desplegar una fila no pide nada al servidor — no hay un
// `useTicket(id)` para leer, porque no haría falta ninguno.
//
// Lo único que el detalle agrega es el booleano `classified`, y ese se deriva
// de `classified_by` en `utils/tickets.js`. Una consulta entera para recuperar
// un dato que ya está en la mano es una consulta que no se hace.
//
// EL ALCANCE LO RESUELVE EL BACKEND, ticket por ticket. Esta pantalla no filtra
// por proyecto «por seguridad»: pide lo que pide y muestra lo que llega. La
// regla del tier —agrupar tickets en un incidente no le abre a nadie los
// reportes de otro cliente— se sostiene en `services/_lib/ticket_scope.js`, y
// un filtro del navegador que pretendiera ayudar solo taparía si falla.

export const TICKETS_PAGE_SIZE = 25;   // el default del backend; el tope duro es 100

export function useTicketsList({ limit = TICKETS_PAGE_SIZE, offset = 0, projectId, state, kind } = {}) {
    const params = { limit, offset };
    if (projectId) params.project_id = projectId;
    if (state) params.state = state;
    if (kind) params.kind = kind;

    return useQuery({
        queryKey: ticketKeys.list(params),
        queryFn: ({ signal }) => api.get(ticketPaths.list(), { params, signal }).then(r => r.data),
        // Paginar no colapsa la lista a un spinner: la página anterior se queda
        // y se atenúa, así que el salto de layout no existe.
        placeholderData: keepPreviousData,
    });
}

// EL RECUENTO POR ESTADO, Y ES UN TOTAL DE VERDAD.
//
// Tres consultas de `limit: 1` que solo leen `pagination.total`. Podría haberse
// contado sobre la página cargada y habría sido más barato y falso: «3
// recibidos» querría decir «3 en los 25 que trajo esta página», que con 200
// tickets no contesta nada. El backend ya cuenta con el mismo alcance y el
// mismo filtro de proyecto; preguntárselo tres veces cuesta tres filas.
export function useTicketTally({ projectId } = {}) {
    const queries = useQueries({
        queries: TICKET_STATES.map(state => {
            const params = { limit: 1, offset: 0, state: state.key };
            if (projectId) params.project_id = projectId;
            return {
                queryKey: ticketKeys.list(params),
                queryFn: ({ signal }) => api.get(ticketPaths.list(), { params, signal }).then(r => r.data),
                staleTime: 30_000,
            };
        }),
    });

    return TICKET_STATES.map((state, index) => ({
        ...state,
        total: queries[index]?.data?.pagination?.total ?? null,
        isLoading: queries[index]?.isLoading ?? false,
    }));
}

// Registrar lo que alguien reportó. Nace `recibido` y sin clasificar: recibir
// no confirma un error ni aprueba una sugerencia.
export function useCreateTicket() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => api.post(ticketPaths.create(), payload).then(r => r.data),
        onSuccess: () => qc.invalidateQueries({ queryKey: ticketKeys.all }),
    });
}

// Clasificar: alguien lo miró y dijo qué es. Deja autor y fecha, y mueve
// `recibido → clasificado`. No crea incidentes ni tareas — el tier lo prohíbe
// explícitamente, y por eso esta mutación no toca ninguna otra caché.
export function useClassifyTicket(id) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => api.post(ticketPaths.classify(id), payload).then(r => r.data),
        onSuccess: () => qc.invalidateQueries({ queryKey: ticketKeys.all }),
    });
}

// Resolver, para quien reportó. Explícito y de a uno: resolver un INCIDENTE no
// actualiza sus tickets, porque la solución puede no estar disponible para
// todos los entornos y usuarios a la vez.
export function useResolveTicket(id) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => api.post(ticketPaths.resolve(id), payload).then(r => r.data),
        onSuccess: () => qc.invalidateQueries({ queryKey: ticketKeys.all }),
    });
}
